import React, { useState, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, Sparkles, Leaf, Flame, Tag } from 'lucide-react';
export type Bonus = {
  number: string;
  title: string;
  subtitle: string;
  image: string;
  imageAlt: string;
  realValue: string;
  hook: React.ReactNode;
  bullets: string[];
  testimonial: {
    quote: string;
    name: string;
    location: string;
    Icon: ComponentType<{
      className?: string;
      strokeWidth?: number;
    }>;
  };
};
export const BONUSES: Bonus[] = [{
  number: '01',
  title: 'Protocolo SOS Antojos',
  subtitle: 'Cómo apagar la ansiedad por dulce, los antojos nocturnos y el hambre emocional sin usar fuerza de voluntad',
  image: 'https://cdn.magicpatterns.com/uploads/dLdnND6p7giAJGVK2DMHjg/Bono_1.png',
  imageAlt: 'Taza de té caliente con un cuadrito de chocolate sobre una mesa de madera',
  realValue: '$17',
  hook: <>
        <p className="mb-4" data-id="element-236">
          ¿Sabes ese momento en que son las 10 de la noche, cenaste bien, no
          tienes hambre real, pero algo dentro de ti exige chocolate o pan?
        </p>
        <p className="mb-4" data-id="element-237">
          No es debilidad. Es tu cortisol ganándole la batalla a tu voluntad.
        </p>
        <p data-id="element-238">
          El Protocolo SOS Antojos te da un sistema de intervención de 5 minutos
          para apagar ese ciclo en tiempo real.
        </p>
      </>,
  bullets: ['El Algoritmo SOS de 5 Pasos para interrumpir un antojo antes de que gane', 'Los 7 disparadores hormonales que activan tus antojos sin que lo notes', '10 Snacks Hormonales Inteligentes que satisfacen sin disparar tu insulina', 'Tu Kit de Emergencia Antiantojos para casa, oficina y bolso', 'La Rutina Nocturna Anticraving para cerrar el día sin episodios de hambre emocional', 'Un Mini Plan de 3 Días para estabilizar tus cravings desde esta semana'],
  testimonial: {
    quote: 'Después de entender por qué tenía antojos, dejé de pelear contra ellos. Empecé a responderles diferente. En una semana los antojos nocturnos desaparecieron casi por completo.',
    name: 'Valentina R.',
    location: 'Colombia',
    Icon: Sparkles
  }
}, {
  number: '02',
  title: 'Recetario Hormonal de 15 Minutos',
  subtitle: 'Comidas rápidas que estabilizan tu energía, reducen inflamación y equilibran tus hormonas en tiempo récord',
  image: 'https://cdn.magicpatterns.com/uploads/ay7dGFBXr4zUJytSYWyuQQ/Bono_2.png',
  imageAlt: 'Plato saludable con aguacate, espinacas, salmón ahumado, huevo poché y nueces',
  realValue: '$17',
  hook: <>
        <p className="mb-4" data-id="element-239">
          La mayor objeción que escuchamos de mujeres como tú es esta:
        </p>
        <p className="mb-4 italic text-sand-dark" data-id="element-240">
          "No tengo tiempo para cocinar sano."
        </p>
        <p className="mb-4" data-id="element-241">Este recetario existe precisamente para eso.</p>
        <p data-id="element-242">
          Cada receta fue diseñada con una sola regla: Proteína + Grasa
          Saludable + Fibra Verde. En menos de 15 minutos reales.
        </p>
      </>,
  bullets: ['5 Desayunos Rápidos que anclan tu glucosa desde la mañana y eliminan el antojo de media mañana', '5 Almuerzos Anticrash que eliminan el bajón de energía de las 3 PM', '5 Cenas Reparadoras que activan la quema de grasa nocturna y mejoran tu sueño', '5 Snacks Funcionales para proteger tus tardes sin remordimiento', '5 Bebidas e Infusiones Hormonales para reducir cortisol e inflamación durante el día', 'El Meal Prep del 1%: 20 minutos el domingo para salvar toda tu semana', 'Lista completa de ingredientes base para tener siempre en casa'],
  testimonial: {
    quote: 'Nunca pensé que comer hormonalmente inteligente podía ser tan simple. Las recetas son riquísimas y las preparo mientras hablo por teléfono.',
    name: 'Carolina M.',
    location: 'México',
    Icon: Leaf
  }
}, {
  number: '03',
  title: 'Checklist de Supervivencia Hormonal',
  subtitle: 'Tu hoja de ruta diaria para recuperar energía, reducir inflamación y equilibrar tus hormonas sin pensar demasiado',
  image: 'https://cdn.magicpatterns.com/uploads/mvifc5kRZitubq5wr4qfxg/Bono_3.png',
  imageAlt: 'Tablet con una hoja de ruta diaria sobre un escritorio minimalista con planta y vaso de agua con limón',
  realValue: '$17',
  hook: <>
        <p className="mb-4" data-id="element-243">Saber qué hacer no es suficiente.</p>
        <p className="mb-4" data-id="element-244">
          El verdadero problema es recordarlo y hacerlo cuando estás cansada,
          ocupada y con el cerebro en modo automático.
        </p>
        <p className="mb-4" data-id="element-245">
          La Checklist de Supervivencia Hormonal convierte todo el método en una
          hoja de ruta visual que puedes seguir sin pensar.
        </p>
        <p data-id="element-246">
          Una victoria pequeña a la vez. Un check a la vez. Un 1% mejor cada
          día.
        </p>
      </>,
  bullets: ['Checklist Matutino para activar tu metabolismo y frenar el cortisol desde que abres los ojos', 'Checklist de Tarde para proteger tu energía en las horas más vulnerables del día', 'Checklist Nocturno para preparar tu cuerpo para la reparación hormonal durante el sueño', 'Rastreador de Síntomas Semanal para que veas con tus propios ojos cómo mejoras semana a semana', 'Señales de Mejora que debes observar más allá del peso y la báscula', 'Versión Resumen de Nevera: una sola hoja que puedes imprimir y pegar en tu refrigerador', 'Tu Contrato de Compromiso del 1% para hacer oficial tu decisión de cambiar'],
  testimonial: {
    quote: 'Esta checklist es lo que me faltaba. Tengo la guía completa pero sin esto no sabía por dónde empezar cada día. Ahora es automático.',
    name: 'Sofía L.',
    location: 'España',
    Icon: Flame
  }
}];
type BonusDetailCardProps = {
  bonus: Bonus;
  reversed?: boolean;
  compact?: boolean;
};
/**
 * Reusable detailed card for one bonus. Shows a short summary by default
 * (title + subtitle + price badge) and reveals the full hook/bullets/testimonial
 * when the user clicks "Leer más". Used by both the dedicated Bonuses section
 * AND the collapsible panel inside the Bundle section.
 */
export function BonusDetailCard({
  bonus,
  reversed = false
}: BonusDetailCardProps) {
  const [expanded, setExpanded] = useState(false);
  return <article className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center bg-white border border-ink/5 shadow-soft p-6 md:p-8 lg:p-10 transition-shadow duration-500 hover:shadow-soft-lg`} data-id="element-247">
      {/* Image — visible only on mobile.  Hidden from md+ to give bonus
            copy the full editorial column on desktop. */}
      <div className="relative w-full max-w-[220px] sm:max-w-[260px] mx-auto aspect-square md:hidden" data-id="element-248">
        <div className="absolute -inset-2 bg-sage/30 rounded-xl -rotate-2" data-id="element-249"></div>
        <div className="relative aspect-square overflow-hidden rounded-xl shadow-soft-lg" data-id="element-250">
          <img src={bonus.image} alt={bonus.imageAlt} width={500} height={500} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" data-id="element-251" />
        </div>
      </div>

      {/* Content */}
      <div className="min-w-0 text-left md:col-span-12" data-id="element-252">
        <div className="inline-flex items-center gap-1.5 uppercase tracking-[0.18em] text-[10px] text-bordo bg-bordo/10 px-2 py-0.5 mb-2" data-id="element-253">
          <Tag strokeWidth={1.5} className="w-3 h-3" data-id="element-254" />
          <span translate="no" data-id="element-255">
            Bono · {bonus.number} · Valor real{' '}
            <span style={{
            textDecoration: 'line-through',
            textDecorationColor: '#722F37',
            textDecorationThickness: '2px'
          }} data-id="element-256">
              {bonus.realValue}
            </span>{' '}
            · Hoy GRATIS
          </span>
        </div>

        <h3 className="font-serif text-2xl md:text-3xl text-ink mb-3 leading-tight" data-id="element-257">
          {bonus.title}
        </h3>

        <p className="text-base text-ink-light font-light leading-relaxed mb-5" data-id="element-258">
          {bonus.subtitle}
        </p>

        {/* Read more / less toggle — minimal, delicate text link */}
        <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} aria-controls={`bonus-${bonus.number}-detail`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-bordo hover:text-bordo-dark border-b border-bordo/40 hover:border-bordo pb-1 transition-colors duration-300" data-id="element-259">
          <span data-id="element-260">{expanded ? 'Leer menos' : 'Leer más'}</span>
          <ChevronDown strokeWidth={1.5} className={`w-3.5 h-3.5 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} data-id="element-261" />
        </button>

        {/* Expanded details */}
        <AnimatePresence initial={false} data-id="element-262">
          {expanded && <motion.div id={`bonus-${bonus.number}-detail`} initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.4,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="overflow-hidden" data-id="element-263">
              <div className="pt-7 text-left" data-id="element-264">
                <div className="text-base text-ink-light font-light leading-relaxed mb-7" data-id="element-265">
                  {bonus.hook}
                </div>

                <div className="mb-8" data-id="element-266">
                  <p className="uppercase tracking-[0.2em] text-xs text-ink-light mb-4" data-id="element-267">
                    Dentro de este bono encontrarás
                  </p>
                  <ul className="space-y-3" data-id="element-268">
                    {bonus.bullets.map((b, i) => <li key={i} className="flex items-start gap-3" data-id="element-269">
                        <ChevronRight strokeWidth={1.5} className="w-4 h-4 mt-1 flex-shrink-0 text-sand-dark" data-id="element-270" />
                        <span className="text-base text-ink-light font-light leading-relaxed" data-id="element-271">
                          {b}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <div className="bg-sage/30 border-l-2 border-sand p-6 md:p-7" data-id="element-272">
                  <bonus.testimonial.Icon strokeWidth={1.5} className="w-5 h-5 text-sand-dark mb-3" data-id="element-273" />
                  <p className="font-serif text-lg md:text-xl italic text-ink leading-relaxed mb-3" data-id="element-274">
                    "{bonus.testimonial.quote}"
                  </p>
                  <p className="text-xs uppercase tracking-[0.15em] text-ink-light" data-id="element-275">
                    <span className="text-ink font-medium" data-id="element-276">
                      {bonus.testimonial.name}
                    </span>{' '}
                    · {bonus.testimonial.location}
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </article>;
}